'use client';

import React, { useState, useEffect } from 'react';
import { X, Calculator, ChevronDown, Sparkles } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

// ─── BANNER SWITCH ────────────────────────────────────────────────────────────
// Change this one value to swap the active banner. Push to deploy.
const ACTIVE_BANNER: 'calculator' | 'early-bird' = 'early-bird';
// Master on/off switch. Set to true to show the active banner again.
const BANNER_ENABLED: boolean = false;
// ─────────────────────────────────────────────────────────────────────────────

const BANNER_CONFIG = {
  'calculator': {
    icon: Calculator,
    textLong: 'Calculate Your Real Mortgage Marketing ROI',
    textMedium: 'Calculate Your Marketing ROI',
    textShort: 'Calculate Your Marketing ROI',
    buttonDesktop: 'Run the Numbers',
    buttonMobile: 'Calculate',
    href: '/mortgage-marketing-roi-calculator',
    ariaLabel: 'ROI Calculator announcement',
    excludedPaths: [
      '/mortgage-marketing-roi-calculator',
      '/mortgage-lead-generation-demo',
      '/mortgage-marketing-pricing',
    ],
  },
  'early-bird': {
    icon: Sparkles,
    textLong: 'rebel Ai is live — AI-powered mortgage marketing tools, built for loan officers',
    textMedium: 'rebel Ai is live — see what we built',
    textShort: 'rebel Ai is live',
    buttonDesktop: 'Get Started Free',
    buttonMobile: 'Get Started',
    href: '/ai',
    ariaLabel: 'rebel Ai launch announcement',
    excludedPaths: [
      '/ai',
      '/rebel-iq-ai',
      '/mortgage-lead-generation-demo',
      '/mortgage-marketing-pricing',
    ],
  },
} as const;

/**
 * MortgageROIBanner - Global announcement bar
 *
 * Features:
 * - Expandable/collapsible (no localStorage - resets on refresh)
 * - Small pull tab when collapsed (fixed at top)
 * - Excluded from specific pages (configured per banner)
 * - Mobile responsive with adaptive text
 * - Brand gradient with shimmer animation
 * - Accessibility compliant
 * - Hydration-safe with mounted state
 *
 * To switch banners: change ACTIVE_BANNER above and push.
 */
export const MortgageROIBanner: React.FC = () => {
  const pathname = usePathname();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const config = BANNER_CONFIG[ACTIVE_BANNER];
  const BannerIcon = config.icon;

  // Excluded pages - no banner at all
  const excludedPaths = config.excludedPaths;

  // Check if current path is a share URL (contains /s/)
  const isShareUrl = pathname.includes('/s/');

  // Hydration-safe initialization
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleClose = () => {
    setIsCollapsed(true);
  };

  const handleExpand = () => {
    setIsCollapsed(false);
  };

  // Banner globally disabled — render nothing (desktop + mobile)
  if (!BANNER_ENABLED) {
    return null;
  }

  // Prevent flash of content before hydration
  if (!isMounted) {
    return <div className="h-0" aria-hidden="true" />;
  }

  // Don't render on excluded pages or share URLs
  if ((excludedPaths as readonly string[]).includes(pathname) || isShareUrl) {
    return null;
  }

  return (
    <>
      {/* Collapsed: Slim Full-Width Bar */}
      {isCollapsed && (
        <button
          onClick={handleExpand}
          className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-[#294bfd] to-[#ff34a7] h-5 hover:h-6 flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#294bfd]"
          aria-label="Show ROI Calculator banner"
          type="button"
        >
          <ChevronDown className="w-4 h-4 text-white animate-bounce" />
        </button>
      )}

      {/* Expanded: Full Banner */}
      {!isCollapsed && (
        <div
          className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#294bfd] to-[#ff34a7] shadow-lg animate-slideDown"
          role="banner"
          aria-label="ROI Calculator announcement"
        >
          <div className="h-14 flex items-center justify-between px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            {/* Left: Icon + Text */}
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
              <BannerIcon className="w-5 h-5 text-white flex-shrink-0 hidden sm:block" />
              <span className="text-white font-semibold text-sm sm:text-base truncate">
                <span className="hidden md:inline">{config.textLong}</span>
                <span className="hidden sm:inline md:hidden">{config.textMedium}</span>
                <span className="sm:hidden">{config.textShort}</span>
              </span>
            </div>

            {/* Right: Button + Close */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <Link
                href={config.href}
                className="roi-banner-button"
                aria-label={config.ariaLabel}
              >
                <span className="hidden sm:inline">{config.buttonDesktop}</span>
                <span className="sm:hidden">{config.buttonMobile}</span>
              </Link>

              <button
                onClick={handleClose}
                className="text-white hover:bg-white/20 rounded-lg p-1.5 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#294bfd]"
                aria-label="Close banner"
                type="button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spacer to prevent content from hiding under fixed banner */}
      {!isCollapsed && <div className="h-14" aria-hidden="true" />}
      {isCollapsed && <div className="h-5" aria-hidden="true" />}
    </>
  );
};
